import Tooltip from '@Components/Feedback/Tooltip';
import TooltipContent from '@Components/Feedback/Tooltip/Content';
import TooltipTrigger from '@Components/Feedback/Tooltip/Trigger';
import { cn } from "@Core/Helpers";
import React, { ComponentProps } from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors " +
    "hover:no-underline " +
    "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 " +
    "disabled:pointer-events-none disabled:opacity-50 " +
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary/90",
                danger: "bg-destructive text-white hover:bg-destructive/90",
                outline: "border border-gray-100 border-input bg-background hover:bg-accent hover:text-accent-foreground",
                secondary: "border border-gray-100 bg-secondary text-secondary-foreground hover:bg-secondary/80",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                link: "text-sky-800 hover:underline",
                success: 'bg-green-600 text-white hover:bg-green-600/90',
                info: 'bg-blue-600 text-white hover:bg-blue-600/90',
            },
            size: {
                default: "h-10 px-4 py-2",
                xs: "h-8 px-2 py-1",
                sm: "h-9 rounded-md px-3",
                lg: "h-11 rounded-md px-8",
                icon: "h-10 w-10",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
)

export interface ButtonProps extends ComponentProps<"button">, VariantProps<typeof buttonVariants> {
    asChild?: boolean,
    tooltip?: string,
    tooltipSide?: 'top' | 'right' | 'bottom' | 'left',
}

const Button = ({
    className,
    variant,
    size,
    asChild = false,
    tooltip = undefined,
    tooltipSide = 'top',
    ...props
}: ButtonProps) => {
    const Comp = asChild ? Slot : 'button'

    const output = (
        <Comp
            data-slot="button"
            data-variant={variant}
            data-size={size}
            className={cn(buttonVariants({ variant, size, className }))}
            {...props}
        />
    );

    if (!tooltip) {
        return output;
    }

    return (
        <Tooltip>
            <TooltipTrigger>{output}</TooltipTrigger>

            <TooltipContent side={tooltipSide}><p>{tooltip}</p></TooltipContent>
        </Tooltip>
    );
}

Button.displayName = "Button"

export { Button, buttonVariants }
